import { useEffect, useRef, useState } from "react";
import ajPhoto from "@/assets/aj-grimaldi.jpeg";
import ajSignature from "@/assets/aj-signature.png";

const FounderSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative py-10 md:py-12 bg-background overflow-hidden"
    >
      {/* Subtle background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Eyebrow */}
          <span
            className={`inline-block text-primary text-xs font-semibold tracking-wider uppercase mb-3 transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            The Person Behind the Pixels
          </span>

          {/* Headline */}
          <h2
            className={`text-2xl md:text-3xl font-bold leading-tight mb-2 transition-all duration-700 delay-100 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Agency Quality.{" "}
            <span className="text-gradient-static">Freelance Agility.</span>
          </h2>

          {/* Subheadline */}
          <p
            className={`text-base text-muted-foreground font-medium mb-4 transition-all duration-700 delay-150 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Meet AJ, the developer who builds your site.
          </p>

          {/* Content with floating image */}
          <div
            className={`transition-all duration-700 delay-200 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            {/* Floating Photo */}
            <div className="float-right ml-6 mb-4 w-32 md:w-40">
              <div className="relative animate-float group" style={{ animationDuration: '5s' }}>
                {/* Glow effect - intensifies on hover */}
                <div className="absolute -inset-3 bg-gradient-to-br from-primary/30 to-accent/30 rounded-xl blur-xl opacity-50 transition-all duration-500 group-hover:opacity-80 group-hover:blur-2xl group-hover:-inset-4" />
                
                {/* Secondary pulsing glow */}
                <div className="absolute -inset-2 bg-primary/20 rounded-xl blur-lg animate-[pulse_3s_ease-in-out_infinite] opacity-40" />
                
                {/* Main image */}
                <div className="relative aspect-square rounded-xl overflow-hidden ring-1 ring-foreground/10 shadow-lg transition-shadow duration-500 group-hover:shadow-xl group-hover:shadow-primary/20">
                  <img 
                    src={ajPhoto} 
                    alt="AJ Grimaldi - Founder of Wave & Wire"
                    width={160}
                    height={160}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                  />
                </div>

                {/* Decorative corner */}
                <div className="absolute -bottom-2 -right-2 w-8 h-8 border-2 border-primary/30 rounded-lg transition-all duration-300 group-hover:border-primary/60 group-hover:-bottom-3 group-hover:-right-3" />
              </div>
            </div>

            {/* Body Text - wraps around photo */}
            <div className="text-sm md:text-base text-muted-foreground leading-relaxed space-y-3">
              <p>
                I never planned to start an agency. I just wanted a great website for my own real estate business. But I was shocked when agencies quoted me up to{" "}
                <span className="text-foreground font-medium">$40,000 for standard designs</span>. On top of that, they wanted huge monthly fees just to host and manage it.
              </p>
              <p>
                Let's be real. I am not your stereotypical rich Hamptons guy. If you are looking for a corporate executive with a Rolex and the perfect life, that isn't me. I don't even have professional headshots! I was told this part needs to be super professional and to look the part that I'm selling.{" "}
                <span className="text-foreground font-medium">I'm not going to pretend to be someone I'm not</span>. I'm a regular guy just like you. I actually still work in real estate, but I started Wave & Wire because this stuff is genuinely fun for me. I'm not here to empty your pockets like the big agencies. I just want to help build something great with you.
              </p>
              <p>
                I created this business to stop small businesses from paying what I call{" "}
                <span className="text-foreground font-medium">hostage fees</span>. I believe you should own your website, not rent it.
              </p>
              <p>
                I explain the tech in plain English using the industry I know best. I view your website like real estate. The domain is your street address, the hosting is the land, and the site is the house.
              </p>
              <p>
                I build the custom house, ensure the SEO foundation is solid, and simply hand you the keys. You get a{" "}
                <span className="text-foreground font-medium">Manhattan-quality site with Hamptons-local service</span>, and you never have to worry about surprise costs again.
              </p>
            </div>

            {/* Clear float */}
            <div className="clear-both" />
          </div>

          {/* Signature */}
          <div
            className={`pt-4 mt-4 border-t border-border/50 transition-all duration-700 delay-300 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            <div className="flex items-center gap-4">
              {/* Gold signature image */}
              <img 
                src={ajSignature} 
                alt="AJ Grimaldi signature"
                width={91}
                height={48}
                loading="lazy"
                decoding="async"
                className="h-12 md:h-14 w-auto"
              />
              <div className="text-xs text-muted-foreground">
                <p className="font-medium text-foreground">AJ Grimaldi</p>
                <p>Founder, Wave & Wire</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FounderSection;
